'use client';

import { appendOneTrustScriptTag, isLottolandNativeAppUserAgent } from '@shared/native';
import { useEffect } from 'react';

const STACKING_LAYER_ID = 'onetrust-stacking-layer';
const ONE_TRUST_CONTAINER_ID = 'onetrust-consent-sdk';

const STACKING_LAYER_STYLES: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483647,
  pointerEvents: 'none',
};

const ONE_TRUST_CONTAINER_STYLES: React.CSSProperties = {
  pointerEvents: 'auto',
};

type OneTrustConditionalLoaderProps = {
  domainScript: string;
};

function hideOneTrustContainers(): void {
  document.getElementById(STACKING_LAYER_ID)?.setAttribute('hidden', '');
  document.getElementById(ONE_TRUST_CONTAINER_ID)?.setAttribute('hidden', '');
}

/**
 * Loads the OneTrust cookie consent stub only when the app is not in an embedded
 * context (e.g. Pragmatic iframe). In iframes we set __COOKIE_CONSENT_EMBEDDED__
 * so ConsentManager uses cookie-based consent and the banner is never shown.
 *
 * iOS Hybrid WebView skips the stub and hides the banner containers. Native script
 * installation lives in `NativeScripts`.
 *
 * Renders a styled wrapper and the container div OneTrust expects (onetrust-consent-sdk).
 * The stub script populates that container when it loads, so the banner renders directly
 * into our div with no node moving.
 */
function OneTrustConditionalLoader({ domainScript }: OneTrustConditionalLoaderProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isLottolandNativeAppUserAgent(navigator.userAgent)) {
      window.__COOKIE_CONSENT_EMBEDDED__ = true;
      hideOneTrustContainers();
      return;
    }

    if (window.self !== window.top) {
      window.__COOKIE_CONSENT_EMBEDDED__ = true;
      return;
    }

    appendOneTrustScriptTag(domainScript);
  }, [domainScript]);

  return (
    <div id={STACKING_LAYER_ID} style={STACKING_LAYER_STYLES} aria-hidden>
      <div id={ONE_TRUST_CONTAINER_ID} style={ONE_TRUST_CONTAINER_STYLES}></div>
    </div>
  );
}

export { OneTrustConditionalLoader };
